import { HARNESS_CATALOG } from "./harnessCatalog.js";

/** @typedef {'python' | 'typescript' | 'javascript'} SdkLanguage */
/**
 * @template T
 * @typedef {({status: 'known', value: T} | {status: 'unknown' | 'conflicting' | 'mixed', value: null}) &
 * {values: T[], evidence: object[], unavailable: number, truncated: boolean}} EvidenceDimension
 */
/** @typedef {{sdkLanguage: EvidenceDimension<SdkLanguage>} & Record<string, EvidenceDimension<string>>} SourceEvidenceDimensions */

export const SdkLanguage = Object.freeze({ PYTHON: "python", TYPESCRIPT: "typescript", JAVASCRIPT: "javascript" });

// Closed vocabularies are validated at runtime, including facts read from ProtoJSON.
export const EVIDENCE_DIMENSIONS = Object.freeze({
  host: { label: "Host application", values: HARNESS_CATALOG.hostLabels },
  interface: { label: "Interface", values: { web: "Web", desktop: "Desktop app", mobile: "Mobile app", cli: "CLI", api: "API", addin: "Add-in" } },
  operatingSystem: { label: "Operating system", values: { macos: "macOS", windows: "Windows", ios: "iOS", android: "Android" } },
  sdkLanguage: { label: "SDK language", values: { [SdkLanguage.PYTHON]: "Python", [SdkLanguage.TYPESCRIPT]: "TypeScript", [SdkLanguage.JAVASCRIPT]: "JavaScript" } },
  workload: { label: "Workload", values: { bigquery: "BigQuery" } },
  instrumentationSdk: { label: "Instrumentation SDK" },
  framework: { label: "Instrumentation library" },
  runtime: { label: "Workload runtime" },
  captureSource: { label: "Captured via", values: { openai_sync: "OpenAI Compliance", anthropic_sync: "Anthropic Compliance", copilot_sync: "Microsoft 365", perplexity_sync: "Perplexity audit logs", langsmith_sync: "LangSmith", vertex_sync: "Vertex AI", origin_dashboard: "Origin Dashboard" } },
  clientKind: { label: "Client category", values: { first_party: "First-party", third_party: "Third-party" } },
});

export function evidenceValueLabel(dimension, value) {
  const spec = Object.hasOwn(EVIDENCE_DIMENSIONS, dimension) ? EVIDENCE_DIMENSIONS[dimension] : null;
  if (!spec || typeof value !== "string" || !value.trim() || value.length > 1024) return null;
  return spec.values ? (Object.hasOwn(spec.values, value) ? spec.values[value] : null) : value;
}

export function resolveEvidenceDimension(dimension, claims = [], conflict = false) {
  const evidence = claims.map(claim => ({ ...claim, value: evidenceValueLabel(dimension, claim.value) ? claim.value : null }));
  const values = [...new Set(evidence.map(claim => claim.value).filter(value => value !== null))].sort();
  const status = conflict || values.length > 1 ? "conflicting"
    : values.length === 1 && evidence.every(claim => claim.value !== null) ? "known" : "unknown";
  return { status, value: status === "known" ? values[0] : null, values, evidence, unavailable: status === "unknown" ? 1 : 0, truncated: false };
}

export function summarizeEvidenceDimensions(observations, truncated = false) {
  return Object.fromEntries(Object.keys(EVIDENCE_DIMENSIONS).map(key => {
    const dimensions = observations.map(observation => observation.dimensions[key]);
    const values = [...new Set(dimensions.flatMap(dimension => dimension.status === "known" ? [dimension.value] : []))].sort();
    const conflict = dimensions.some(dimension => dimension.status === "conflicting");
    const unavailable = dimensions.filter(dimension => dimension.status === "unknown").length;
    const status = conflict ? "conflicting" : values.length > 1 ? "mixed"
      : values.length === 1 && !unavailable && !truncated ? "known" : "unknown";
    return [key, { status, value: status === "known" ? values[0] : null, values,
      evidence: dimensions.flatMap(dimension => dimension.evidence), unavailable, truncated }];
  }));
}

export function evidenceDimensionLines(dimensions) {
  return Object.entries(dimensions || {}).flatMap(([key, dimension]) => {
    if (!dimension.evidence.length) return [];
    const labels = dimension.values.map(value => evidenceValueLabel(key, value)).filter(Boolean);
    const detail = dimension.status === "conflicting" ? "conflicting evidence"
      : labels.length ? labels.join(", ") + (dimension.unavailable || dimension.truncated ? "; others unknown" : "") : "unknown";
    return [{ key, source: EVIDENCE_DIMENSIONS[key].label, detail, evidence: dimension.evidence }];
  });
}

export function identityEvidenceDimensions(identity, fact) {
  return Object.fromEntries(Object.keys(EVIDENCE_DIMENSIONS).map(key => [key,
    resolveEvidenceDimension(key, identity?.[key] ? [{ value: identity[key], fact }] : []),
  ]));
}

export function evidenceProvenanceLines(claims) {
  const lines = [...new Set(claims.flatMap(claim => claim.facts || (claim.fact ? [claim.fact] : [])).map(fact => {
    const provenance = fact.provenance || {};
    const source = provenance.sourcePath || fact.name;
    const value = fact.stringValue ?? fact.booleanValue;
    return [source, value == null ? null : String(value), provenance.sourceKind,
      provenance.observationId && `observation ${provenance.observationId}`,
      provenance.bindingId && `binding ${provenance.bindingId}`].filter(Boolean).join(" | ");
  }))];
  return lines.length > 8 ? [...lines.slice(0, 8), `${lines.length - 8} more evidence references`] : lines;
}
